import { PILAR_LABEL, QURAN_CREDIT, REFLECTION_MAX_CHARS } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronRight, Info, Lock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj, type ObjId } from '../../components/Obj.tsx'
import { SkCard, SkRows, SkVerses } from '../../components/Skeleton.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { ApiError } from '../../lib/api.ts'
import { sparkBurst } from '../../lib/motion.ts'
import { useToast } from '../../lib/toast.ts'
import { PassageBlock } from '../pegangan/PassageBlock.tsx'
import { useGarden } from './LevelUp.tsx'
import { Back, JadikanNiat } from './parts.tsx'
import { completeDay, refreshPerjalanan, startProgram, useDay, usePrograms, useProgram, type ProgramItem, type RunState } from './perjalanan.ts'

const msg = (e: unknown) => (e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')
const DRAFT = 'Draf: tantangan ini masih menunggu tinjauan ustadz.'
/** The action part of a challenge ("Ayat ini menyebut …. Hari ini, …" → "Hari ini, …"), for a niat. */
export const stepOf = (t: string) => /^Ayat ini[^.]*?[.”]\s+(.+)$/s.exec(t)?.[1] ?? t

const status = (run: RunState | null) =>
  !run ? 'Belum dimulai'
  : run.finished ? 'Selesai 7 hari'
  : run.openToday ? `Hari ke-${run.next} terbuka`
  : `Hari ke-${run.next} terbuka besok`

function Dots({ run }: { run: RunState | null }) {
  return (
    <span className="pj-days" aria-hidden="true">
      {Array.from({ length: 7 }, (_, i) => <i key={i} className={run?.done.includes(i + 1) ? 'on' : run && i + 1 === run.next ? 'next' : ''} />)}
    </span>
  )
}

/** /perjalanan/tantangan — the programs, the ones in progress first. */
export function TantanganListPage() {
  const { data, isPending, isError, refetch } = usePrograms()
  const sorted = data ? [...data.aktif, ...data.programs.filter((p) => !data.aktif.includes(p))] : []
  return (
    <div className="page pj">
      <Back />
      <h1>Tantangan 7 hari</h1>
      <p className="muted" style={{ marginTop: -6 }}>Tujuh hari, tiap hari satu ayat dan satu langkah kecil. Hari berikutnya terbuka besok.</p>
      {isPending && <SkRows label="Memuat tantangan" rows={4} />}
      {isError && (
        <EmptyState obj="pijakan" title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && data.programs.length === 0 && <EmptyState obj="pijakan" title="Tantangan sedang disiapkan">Isinya masih ditinjau. Coba lagi nanti, ya.</EmptyState>}
      {sorted.length > 0 && (
        <div className="stack" style={{ marginTop: 14 }}>
          {sorted.map((p: ProgramItem) => (
            <Link key={p.id} className="card row-card tt-card reveal" to={`/perjalanan/tantangan/${p.id}`}>
              <span className="ic obj-ic" aria-hidden="true"><Obj id={p.obj as ObjId} /></span>
              <div>
                <b>{p.judul}</b>{' '}
                <small>{p.ringkas}</small>{' '}
                <small className="tt-st">{PILAR_LABEL[p.pilar]} · {status(p.run)}</small>
                {p.run && <Dots run={p.run} />}
              </div>
              <ChevronRight className="chev" size={18} aria-hidden="true" />
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

/** /perjalanan/tantangan/:id — the seven days, start / continue / again. */
export function TantanganProgramPage() {
  const { id = '' } = useParams()
  const { data, isPending, isError, error, refetch } = useProgram(id)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [toast, show] = useToast()
  const [pending, setPending] = useState(false)

  const start = async () => {
    setPending(true)
    try {
      const { run } = await startProgram(id)
      await refreshPerjalanan(qc)
      navigate(`/perjalanan/tantangan/${id}/${run.next ?? 1}`)
    } catch (e) {
      show(msg(e))
    } finally {
      setPending(false)
    }
  }

  if (isPending) return <div className="page pj"><Back to="/perjalanan/tantangan" label="Tantangan" /><SkCard label="Memuat tantangan" lines={5} /></div>
  if (isError) {
    const gone = error instanceof ApiError && error.status === 404
    return (
      <div className="page pj">
        <Back to="/perjalanan/tantangan" label="Tantangan" />
        <EmptyState obj="pijakan" title={gone ? 'Tantangan tidak ditemukan' : 'Belum bisa dimuat'} action={gone ? undefined : <button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          {gone ? 'Mungkin sedang ditinjau ulang. Pilih tantangan yang lain dulu, ya.' : 'Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.'}
        </EmptyState>
      </div>
    )
  }
  const run = data.run
  const active = run && !run.finished
  return (
    <div className="page pj">
      <Back to="/perjalanan/tantangan" label="Tantangan" />
      <header className="tt-hero reveal">
        <Obj id={data.obj as ObjId} size={88} className="empty-obj" />
        <div>
          <p className="eyebrow">{PILAR_LABEL[data.pilar]} · 7 hari</p>
          <h1>{data.judul}</h1>
          <p className="muted">{data.ringkas}</p>
        </div>
      </header>
      {!data.reviewed && <p className="pg-draft" role="note"><Info size={16} aria-hidden="true" /> {DRAFT}</p>}
      {data.catatan && <p className="pg-note" role="note"><Info size={16} aria-hidden="true" /> {data.catatan}</p>}

      <ol className="card tt-days" aria-label="Tujuh hari">
        {data.days.map((d) => {
          const done = run?.done.includes(d.no)
          const open = active && run!.next === d.no && run!.openToday
          const body = (
            <>
              <span className={`tt-no${done ? ' on' : ''}`} aria-hidden="true">{done ? <Check size={16} strokeWidth={3} /> : d.no}</span>
              <span className="st"><b>Hari ke-{d.no}</b> <small>{d.ref}{done ? ' · selesai' : open ? ' · terbuka hari ini' : active && run!.next === d.no ? ' · terbuka besok' : ''}</small></span>
              {done || open ? <ChevronRight className="chev" size={18} aria-hidden="true" /> : <Lock className="chev" size={16} aria-hidden="true" />}
            </>
          )
          return <li key={d.no}>{done || open ? <Link className="surah-row" to={`/perjalanan/tantangan/${id}/${d.no}`}>{body}</Link> : <span className="surah-row is-locked">{body}</span>}</li>
        })}
      </ol>

      <div className="tt-cta">
        {!run || run.finished ? (
          <button type="button" className="btn btn-primary btn-block" onClick={start} disabled={pending}>{run?.finished ? 'Ulangi tantangan ini' : 'Mulai hari ke-1'}</button>
        ) : run.openToday ? (
          <Link className="btn btn-primary btn-block" to={`/perjalanan/tantangan/${id}/${run.next}`}>Lanjut hari ke-{run.next}</Link>
        ) : (
          <p className="muted" style={{ textAlign: 'center' }}>Hari ke-{run.next} terbuka besok. Sampai jumpa besok, ya.</p>
        )}
      </div>
      <div className="toast-slot" role="status">{toast && <p className="toast">{toast}</p>}</div>
    </div>
  )
}

/** /perjalanan/tantangan/:id/:hari — the verse (dataset), Tafsir Ringkas, the small step, a private reflection. */
export function TantanganDayPage() {
  const { id = '', hari = '' } = useParams()
  const n = Number(hari)
  const valid = Number.isInteger(n) && n >= 1 && n <= 7
  const { data, isPending, isError, error, refetch } = useDay(id, valid ? n : 1)
  const [toast, show] = useToast()
  const { after, overlay } = useGarden()
  const [jawaban, setJawaban] = useState('')
  const [pending, setPending] = useState(false)
  const [finished, setFinished] = useState<RunState | null>(null)

  useEffect(() => { if (data) setJawaban(data.day.jawaban ?? '') }, [data])
  useEffect(() => setFinished(null), [id, n])

  if (!valid) return <Navigate to={`/perjalanan/tantangan/${id}`} replace />
  const back = <Back to={`/perjalanan/tantangan/${id}`} label="Tantangan" />
  if (isPending) return <div className="page pj">{back}<SkVerses label="Memuat hari ini" count={1} /></div>
  if (isError) {
    const e = error instanceof ApiError ? error : null
    const locked = e?.status === 403
    const notStarted = e?.status === 409
    return (
      <div className="page pj">
        {back}
        <EmptyState
          obj={locked ? 'jam-pasir' : 'pijakan'}
          title={locked ? 'Hari ini belum terbuka' : notStarted ? 'Tantangan belum dimulai' : e?.status === 404 ? 'Tidak ditemukan' : 'Belum bisa dimuat'}
          action={locked || notStarted || e?.status === 404 ? <Link className="btn btn-ghost" to={`/perjalanan/tantangan/${id}`}>Lihat tujuh harinya</Link> : <button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}
        >
          {e && e.status !== 0 ? e.message : 'Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.'}
        </EmptyState>
      </div>
    )
  }

  const { program, day } = data
  const finish = async (el: HTMLElement) => {
    setPending(true)
    try {
      const r = await completeDay(id, n, jawaban.trim() || null)
      if (r.firstTime) { sparkBurst(el, 14); setFinished(r.run) } else show('Refleksi tersimpan.')
      after(r.garden)
    } catch (e) {
      show(msg(e))
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="page pj tt-day">
      {back}
      <p className="eyebrow">{program.judul} · hari ke-{day.no} dari 7</p>
      {!program.reviewed && <p className="pg-draft" role="note"><Info size={16} aria-hidden="true" /> {DRAFT}</p>}

      <section className="card pg-sec pg-ayat reveal" aria-labelledby="tt-ayat-h">
        <h2 id="tt-ayat-h" className="section-t">Ayat hari ini</h2>
        <PassageBlock p={day.ayat} id={`tt-${id}-${n}`} what={`ayat ${day.ayat.ref}`} />
      </section>
      {day.tafsir && (
        <section className="card pg-sec reveal" aria-labelledby="tt-tafsir-h">
          <h2 id="tt-tafsir-h">Tafsir Ringkas</h2>
          <p className="prose">{day.tafsir}</p>
          <p className="pg-src"><SourceLink href={day.tafsirUrl}>Tafsir Ringkas Kemenag, {day.ayat.ref}</SourceLink></p>
        </section>
      )}
      <section className="card pg-sec tt-step reveal" aria-labelledby="tt-step-h">
        <h2 id="tt-step-h">Langkah kecil hari ini</h2>
        <p>{day.tantangan}</p>
        <p className="help">{day.catatan}</p>
        <JadikanNiat text={stepOf(day.tantangan)} source="tantangan" sourceRef={`${id}:${n}`} />
      </section>
      <section className="card pg-sec reveal">
        <label className="journal-l" htmlFor="tt-ref">{day.refleksi}</label>
        <textarea id="tt-ref" className="input textarea" rows={4} maxLength={REFLECTION_MAX_CHARS} value={jawaban} onChange={(e) => setJawaban(e.target.value)} aria-describedby="tt-ref-help" />
        <p id="tt-ref-help" className="help">Boleh dikosongkan. Hanya kamu yang bisa membaca jawabanmu.</p>
      </section>

      {finished ? (
        <section className="card tt-done" role="status">
          <Obj id="tunas" size={64} className="empty-obj" />
          <b>{finished.finished ? 'Tujuh hari selesai. Masya Allah.' : `Hari ke-${n} selesai`}</b>
          <p className="muted">{finished.finished ? 'Langkah-langkah kecilmu pekan ini sudah tercatat. Boleh diulang kapan saja.' : 'Hari berikutnya terbuka besok. Semoga langkah kecil ini menemanimu hari ini.'}</p>
          <Link className="btn btn-ghost btn-block" to="/perjalanan">Kembali ke Perjalanan</Link>
        </section>
      ) : (
        <button type="button" className="btn btn-primary btn-block tt-finish" onClick={(e) => void finish(e.currentTarget)} disabled={pending}>
          {pending ? 'Menyimpan…' : day.done ? 'Simpan refleksi' : 'Selesai untuk hari ini'}
        </button>
      )}
      <p className="credit" style={{ marginTop: 14 }}><SourceLink href={day.ayat.url}>{QURAN_CREDIT}</SourceLink>. Bahan renungan, bukan fatwa.</p>
      <div className="toast-slot" role="status">{toast && <p className="toast">{toast}</p>}</div>
      {overlay}
    </div>
  )
}
